import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { parseYMD, formatYMD, formatTimeRange } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";
import { Calendar, MapPin, Users, Sparkles, CheckCircle2, ArrowLeft, ShieldAlert, BookOpen, ExternalLink, Facebook, Linkedin, Mail as MailIcon, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import SEO from "@/components/SEO";

// Fallback cancellation copy — shown on smaller events (sister circles,
// workshops, day events) that don't have a full registration form. Full
// trainings and retreats surface the policy inside their intake flow.
const DEFAULT_CANCELLATION_POLICY =
  "Refunds are available up to 7 days before the event. Cancellations within " +
  "7 days may be transferred to a future Aviana offering as a credit. Aviana " +
  "reserves the right to reschedule or cancel any event — in that case a full " +
  "refund will be issued.";

// Holly's affiliate Amazon link for Soaring with the Soul.
const SOARING_AMAZON_URL =
  "https://www.amazon.com/dp/B0GVPGD51L?&linkCode=sl2&tag=avianayoga0b-20&linkId=39754c5c9178a30c152d18c5703ffc2b&language=en_US&ref_=as_li_ss_tl";

export default function OfferingDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [offering, setOffering] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentType, setPaymentType] = useState("full");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/offerings/${slug}`);
        setOffering(data);
        if (!data.deposit_usd) setPaymentType("full");
      } catch {
        toast.error("Offering not found.");
        navigate("/");
      }
    })();
  }, [slug, navigate]);

  if (!offering) return <div className="pt-40 container-aviana text-aviana-muted">Loading…</div>;

  const isTraining = offering.type === "training";
  const isRetreat = offering.type === "retreat";
  const isCourse = offering.type === "course";
  const hasSisterhood = isRetreat && offering.sisterhood_price_usd;

  // For trainings, route to the new intake form — payment happens after the form.
  // For retreats, route to the retreat-specific intake form.
  // Phase B will hook up the form; for now we keep the old direct-checkout path as fallback.
  const goReserve = async (tier = "solo") => {
    // Allow admin to override the routing per offering via `registration_form`.
    // Falls back to `type` for offerings that don't have the flag set yet.
    const form = offering.registration_form || (
      isTraining ? "teacher_training"
      : isRetreat ? "destination_retreat"
      : offering.type === "event" ? "workshop_class"
      : null
    );

    if (form === "teacher_training") {
      navigate(`/register/${offering.slug}`);
      return;
    }
    if (form === "destination_retreat" || form === "day_retreat") {
      navigate(`/register-retreat/${offering.slug}${tier === "sisterhood" ? "?tier=sisterhood" : ""}`);
      return;
    }
    if (form === "workshop_class") {
      navigate(`/register-event/${offering.slug}`);
      return;
    }
    if (!user || user === false) {
      navigate("/login", { state: { from: `/offering/${slug}` } });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout/session", {
        offering_id: offering.id,
        payment_type: paymentType,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error("No checkout URL");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
      setLoading(false);
    }
  };

  const amountNow = paymentType === "deposit" && offering.deposit_usd ? offering.deposit_usd : offering.price_usd;
  const balanceDays = offering.balance_due_days_before ?? 30;

  // "End-of-summer sale" style flash pricing. Triggered when `early_bird_until`
  // is a future date and there's a higher `price_regular_usd` to revert to on
  // that date. Also drives the countdown timer + strike-through headline.
  const saleEnd = offering.early_bird_until ? parseYMD(offering.early_bird_until) : null;
  const now = new Date();
  const onSale = !!(saleEnd && saleEnd > now && offering.price_regular_usd && offering.price_regular_usd > offering.price_usd);
  const saleLabel = offering.sale_label || "Early-bird pricing";
  const saleEndDisplay = saleEnd ? formatYMD(offering.early_bird_until) : "";
  const balanceDueLine = offering.balance_due_label
    ? `balance due ${offering.balance_due_label}.`
    : balanceDays === 7
      ? "balance due 1 week before the training starts."
      : balanceDays === 14
      ? "balance due 2 weeks before the training starts."
      : `balance due ${balanceDays} days before arrival.`;

  // Spots indicator
  const booked = offering.spots_left < 9999 && offering.capacity ? offering.capacity - offering.spots_left : null;
  const seatsLeft = offering.spots_left < 9999 ? offering.spots_left : null;
  const lowSeats = seatsLeft !== null && seatsLeft > 0 && seatsLeft <= 5;

  return (
    <div className="pt-28 pb-24" data-testid="offering-detail-page">
      <SEO
        title={offering.title}
        description={offering.subtitle || offering.short_description || `${offering.title} — held with Holly Johnson at ${offering.location}.`}
        path={`/offering/${offering.slug}`}
        image={offering.image_url || "/social/og-restorative-ytt.jpg"}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": isCourse ? "Course" : "Event",
          "name": offering.title,
          "description": offering.subtitle || offering.short_description,
          "image": offering.image_url,
          ...(isCourse ? {} : {
            "startDate": offering.start_date && offering.start_time ? `${offering.start_date}T${offering.start_time}` : offering.start_date,
            "endDate": (offering.end_date || offering.start_date) && offering.end_time ? `${offering.end_date || offering.start_date}T${offering.end_time}` : offering.end_date,
            "eventStatus": "https://schema.org/EventScheduled",
            "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
            "location": { "@type": "Place", "name": offering.location },
          }),
          "organizer": { "@id": "https://avianayoga.com/#organization" },
          "offers": {
            "@type": "Offer",
            "price": String(offering.price_usd),
            "priceCurrency": "USD",
            "availability": offering.spots_left > 0 ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
            "url": `https://avianayoga.com/offering/${offering.slug}`,
          },
        }}
      />
      <div className="container-aviana">
        <Link to={`/${offering.type === "course" ? "courses" : offering.type === "training" ? "trainings" : offering.type === "event" ? "events" : "retreats"}`}
              className="inline-flex items-center gap-2 text-sm text-aviana-muted hover:text-aviana-ink mb-8" data-testid="back-link">
          <ArrowLeft size={14} /> Back
        </Link>

        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="rounded-[2rem] overflow-hidden aspect-[4/3] mb-8 bg-aviana-surface">
              <img src={offering.image_url} alt={offering.title} className="w-full h-full object-cover" />
            </div>
            <p className="overline mb-3">{offering.type}</p>
            <h1 className="font-serif text-5xl md:text-6xl text-aviana-ink leading-[1.05]" data-testid="offering-title">{offering.title}</h1>
            <p className="mt-3 text-xl text-aviana-muted font-serif italic">{offering.subtitle}</p>

            {offering.cancelled && (
              <div className="mt-6 rounded-2xl border-2 border-aviana-plum/40 bg-aviana-plum/8 px-5 py-5" data-testid="cancelled-banner">
                <p className="text-[11px] tracking-[0.22em] uppercase text-aviana-plum font-semibold">This training has been cancelled</p>
                <p className="mt-2 text-aviana-ink text-[15px] leading-[1.65]">
                  {offering.cancelled_reason || "This training is no longer taking place. Holly will be in touch personally with anyone who registered."}
                </p>
                <p className="mt-3 text-aviana-inkSoft text-[14px] leading-[1.6]">
                  Looking for an alternative? Explore Holly's other upcoming aerial yoga teacher trainings — including a new outdoor semi-private cohort at Bliss Woods (Sugar Grove, IL) the same weekend.
                </p>
                <Link
                  to="/trainings/aerial-yoga"
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-aviana-plum text-aviana-cream text-[12px] tracking-[0.18em] uppercase hover:bg-aviana-plumDeep transition-colors"
                  data-testid="view-other-trainings-cta"
                >
                  View upcoming trainings →
                </Link>
              </div>
            )}

            {!offering.cancelled && offering.exclusive_to_studio && (
              <div className="mt-6 rounded-2xl border border-aviana-plum/30 bg-aviana-plum/5 px-5 py-4" data-testid="exclusive-studio-banner">
                <p className="text-[11px] tracking-[0.22em] uppercase text-aviana-plum font-medium">Exclusive Training</p>
                <p className="mt-1.5 text-aviana-ink text-[15px] leading-[1.6]">
                  This training is held exclusively for instructors and affiliates of{" "}
                  <strong className="text-aviana-plum">{offering.exclusive_studio_name || "the hosting studio"}</strong>.
                  Registration links are distributed by the studio manager.
                </p>
                <Link
                  to="/host-a-training"
                  className="mt-3 inline-flex items-center gap-1.5 text-[12px] tracking-[0.18em] uppercase text-aviana-goldDeep hover:text-aviana-plum underline underline-offset-4"
                  data-testid="host-your-own-link"
                >
                  Want to host a training at your studio? →
                </Link>
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-aviana-muted">
              {offering.start_date && (
                <span className="inline-flex items-center gap-2"><Calendar size={16} className="text-aviana-gold" />
                  {formatYMD(offering.start_date, { month: "long", day: "numeric", year: "numeric" })}
                  {offering.end_date && offering.end_date !== offering.start_date && ` – ${formatYMD(offering.end_date, { month: "long", day: "numeric" })}`}
                  {formatTimeRange(offering.start_time, offering.end_time) && (
                    <span className="text-aviana-inkSoft" data-testid="offering-time-range">
                      {" · "}{formatTimeRange(offering.start_time, offering.end_time)}
                    </span>
                  )}
                </span>
              )}
              <span className="inline-flex items-center gap-2"><MapPin size={16} className="text-aviana-gold" />{offering.location}</span>
              {seatsLeft !== null && offering.capacity && (
                <span className="inline-flex items-center gap-2" data-testid="spots-summary"><Users size={16} className="text-aviana-gold" />
                  {booked} of {offering.capacity} spots booked · <strong className="text-aviana-plum">{seatsLeft} left</strong>
                </span>
              )}
            </div>

            {offering.schedule_summary && (
              <p className="mt-4 font-serif italic text-[16px] text-aviana-inkSoft leading-[1.55]" data-testid="offering-schedule-summary">
                {offering.schedule_summary}
              </p>
            )}

            {/* Markdown-rendered structured description */}
            <article className="mt-10 prose-aviana" data-testid="offering-description">
              <ReactMarkdown
                components={{
                  h1: ({ node, ...props }) => <h2 className="font-display text-3xl md:text-4xl text-aviana-ink mt-12 mb-4 leading-tight" {...props} />,
                  h2: ({ node, ...props }) => <h2 className="font-display text-3xl md:text-4xl text-aviana-ink mt-12 mb-4 leading-tight" {...props} />,
                  h3: ({ node, ...props }) => <h3 className="font-display text-2xl text-aviana-ink mt-10 mb-3 leading-tight" {...props} />,
                  p:  ({ node, ...props }) => <p  className="text-aviana-inkSoft leading-[1.85] text-[16px] mb-5" {...props} />,
                  ul: ({ node, ...props }) => <ul className="space-y-2.5 my-5 list-none pl-0" {...props} />,
                  li: ({ node, ...props }) => (
                    <li className="flex gap-3 text-aviana-inkSoft leading-[1.7] text-[15px]">
                      <CheckCircle2 size={18} className="text-aviana-gold flex-shrink-0 mt-1" />
                      <span {...props} />
                    </li>
                  ),
                  strong: ({ node, ...props }) => <strong className="text-aviana-plum font-medium" {...props} />,
                  em: ({ node, ...props }) => <em className="italic text-aviana-plum/90" {...props} />,
                }}
              >
                {offering.description || offering.short_description || ""}
              </ReactMarkdown>
            </article>

            {offering.highlights?.length > 0 && (
              <>
                <h3 className="font-display text-3xl text-aviana-ink mt-14 mb-6">What's included</h3>
                <ul className="grid sm:grid-cols-2 gap-3" data-testid="highlights-list">
                  {offering.highlights.map((h, i) => (
                    <li key={i} className="flex gap-3 text-aviana-inkSoft text-[15px] leading-[1.6]">
                      <CheckCircle2 size={18} className="text-aviana-gold flex-shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>

                {/* Recommended reading — only for teacher trainings. Positioned
                    right below "What's included" so students see the book as
                    the natural next step to prepare for the intensive. */}
                {isTraining && (
                  <div className="mt-8 rounded-[1.5rem] border border-aviana-gold/30 bg-aviana-gold/5 p-6 sm:p-7 flex items-start gap-5 flex-wrap sm:flex-nowrap" data-testid="recommended-reading">
                    <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-full bg-aviana-gold/15 text-aviana-goldDeep">
                      <BookOpen size={22} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="overline-plum mb-2">Recommended reading</p>
                      <p className="font-display text-2xl text-aviana-ink leading-tight mb-2">Soaring with the Soul</p>
                      <p className="text-[14px] text-aviana-inkSoft leading-[1.65]">
                        Holly's guidebook covers the full methodology of this training. Studying it ahead of time helps you arrive prepared and get more out of every minute of the intensive.
                      </p>
                      <a
                        href={SOARING_AMAZON_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-plum hover:text-aviana-gold"
                        data-testid="recommended-book-link"
                      >
                        Order on Amazon <ExternalLink size={13} />
                      </a>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Important details — practical logistics: parking, what to bring,
                what to wear, arrival window. Shown as a distinct card so it
                stands apart from the marketing-heavy Highlights list above. */}
            {offering.important_details?.length > 0 && (
              <>
                <h3 className="font-display text-3xl text-aviana-ink mt-14 mb-6">Important details</h3>
                <ul className="grid sm:grid-cols-2 gap-3 rounded-[1.5rem] bg-aviana-cream border border-aviana-border p-6" data-testid="important-details-list">
                  {offering.important_details.map((d, i) => (
                    <li key={i} className="flex gap-3 text-aviana-inkSoft text-[15px] leading-[1.6]">
                      <span className="text-aviana-plum flex-shrink-0 mt-[6px] text-[8px]">◆</span>
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {isCourse && offering.lessons?.length > 0 && (
              <>
                <h3 className="font-display text-3xl text-aviana-ink mt-14 mb-6">Curriculum</h3>
                <div className="space-y-3">
                  {offering.lessons.map((l, i) => (
                    <div key={l.id} className="flex items-center gap-4 p-5 bg-aviana-surface rounded-2xl" data-testid={`lesson-preview-${l.id}`}>
                      <div className="font-display text-2xl text-aviana-gold w-8">{String(i + 1).padStart(2, "0")}</div>
                      <div className="flex-1">
                        <div className="font-display text-xl text-aviana-ink">{l.title}</div>
                        <div className="text-xs text-aviana-muted">{l.duration_min} min</div>
                      </div>
                      <Sparkles size={16} className="text-aviana-gold" />
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Registration CTA — replaces the old cancellation section (which
                lives on the registration page). Keeps momentum toward the
                enroll button rather than surfacing refund policy first. */}
            {(isTraining || isRetreat) && !offering.cancelled && (
              <section className="mt-14 rounded-[2rem] bg-gradient-to-br from-aviana-plum/12 via-aviana-cream to-aviana-gold/10 border border-aviana-plum/25 p-8 sm:p-10 text-center" data-testid="page-cta">
                <p className="overline-plum mb-3">Ready to say yes?</p>
                <h3 className="font-display text-3xl sm:text-4xl text-aviana-ink leading-tight mb-3">Your spot is one click away.</h3>
                <p className="text-aviana-inkSoft text-[15px] leading-[1.7] max-w-xl mx-auto mb-6">
                  {offering.spots_left === 0
                    ? "This cohort is full — join the waitlist and Holly will personally reach out if a spot opens."
                    : offering.spots_left <= 3
                    ? `Only ${offering.spots_left} ${offering.spots_left === 1 ? "spot" : "spots"} remaining. Reserve yours before this cohort closes.`
                    : "Start your registration now. It takes about 8 minutes and your seat is held the moment you submit."}
                </p>
                <button
                  onClick={() => { document.querySelector('[data-testid="booking-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); }}
                  className="btn-gold inline-flex items-center gap-2"
                  data-testid="page-cta-scroll-btn"
                >
                  {offering.spots_left === 0 ? "Join the waitlist →" : "Begin my registration →"}
                </button>
              </section>
            )}

            {/* Cancellation policy — shown on day-events, workshops, courses,
                and sister-circles that don't have a full registration flow.
                Trainings and retreats show this inside their intake instead. */}
            {!(isTraining || isRetreat) && (
              <section className="mt-14 bg-aviana-cream/60 border border-aviana-border rounded-2xl p-7" data-testid="cancellation-policy">
                <div className="flex items-start gap-3">
                  <ShieldAlert size={20} className="text-aviana-plum mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-display text-2xl text-aviana-ink mb-2">Cancellation & refund policy</h3>
                    <p className="text-aviana-inkSoft leading-[1.75] text-[15px]">{offering.cancellation_policy || DEFAULT_CANCELLATION_POLICY}</p>
                  </div>
                </div>
              </section>
            )}

            {/* Share row — appears at the bottom of every offering. Same UX
                pattern as the blog article footer. Encourages Holly (and any
                fan of the offering) to spread the word in one tap. */}
            <OfferingShareRow title={offering.title} slug={offering.slug} />
          </div>

          {/* Sticky booking card */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 bg-white rounded-[2rem] border border-aviana-border p-9" data-testid="booking-card">
              {offering.cancelled ? (
                <>
                  <p className="overline-plum mb-3">Training cancelled</p>
                  <p className="font-display text-3xl text-aviana-ink leading-tight mb-4">No longer available.</p>
                  <p className="text-aviana-inkSoft text-[14px] leading-[1.65] mb-5">
                    {offering.cancelled_reason || "Holly will be in touch personally with anyone who registered."}
                  </p>
                  <Link
                    to="/trainings/aerial-yoga"
                    className="btn-primary w-full"
                    data-testid="view-other-trainings-btn"
                  >
                    See upcoming trainings →
                  </Link>
                  <p className="text-[12px] text-aviana-muted text-center mt-4 leading-relaxed">
                    Questions? Email <a href="mailto:holly@avianayoga.com" className="text-aviana-plum hover:text-aviana-goldDeep underline underline-offset-2">holly@avianayoga.com</a>
                  </p>
                </>
              ) : hasSisterhood ? (
                <>
                  <p className="overline mb-3">Pricing — per person</p>
                  <div className="space-y-3 mb-6" data-testid="retreat-tier-cards">
                    <div className="p-5 rounded-2xl border border-aviana-border bg-aviana-cream/40">
                      <div className="flex items-baseline justify-between mb-1">
                        <p className="overline-plum">Solo</p>
                        <p className="font-display text-3xl text-aviana-ink">${offering.price_usd.toLocaleString()}</p>
                      </div>
                      <p className="text-aviana-muted text-[12px] leading-[1.55] mb-3">All-inclusive · per person</p>
                      <button
                        onClick={() => goReserve("solo")}
                        disabled={loading || offering.spots_left === 0}
                        className="btn-primary w-full !py-2.5 !text-[11px]"
                        data-testid="reserve-solo-btn"
                      >
                        {offering.spots_left === 0 ? "Waitlist" : "Reserve solo →"}
                      </button>
                    </div>
                    <div className="p-5 rounded-2xl border border-aviana-gold/40 bg-aviana-gold/5">
                      <div className="flex items-baseline justify-between mb-1">
                        <p className="overline-plum text-aviana-goldDeep">Sisterhood</p>
                        <p className="font-display text-3xl text-aviana-ink">${offering.sisterhood_price_usd.toLocaleString()}</p>
                      </div>
                      <p className="text-aviana-muted text-[12px] leading-[1.55] mb-3">
                        Save ${(offering.price_usd - offering.sisterhood_price_usd).toLocaleString()} each when you register with a sister.
                      </p>
                      <button
                        onClick={() => goReserve("sisterhood")}
                        disabled={loading || offering.spots_left === 0}
                        className="btn-gold w-full !py-2.5 !text-[11px]"
                        data-testid="reserve-sisterhood-btn"
                      >
                        {offering.spots_left === 0 ? "Waitlist" : "Reserve sisterhood →"}
                      </button>
                    </div>
                  </div>
                  {offering.deposit_usd && (
                    <p className="text-sm text-aviana-muted mb-2">Reserve your spot with a ${offering.deposit_usd} deposit — {balanceDueLine}</p>
                  )}
                </>
              ) : offering.studio_pays_tuition ? (
                <>
                  <p className="overline mb-3">Tuition</p>
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="font-display text-5xl text-aviana-ink">${offering.price_usd.toLocaleString()}</span>
                    <span className="text-aviana-muted text-sm">USD</span>
                  </div>
                  <p className="text-sm text-aviana-muted italic mb-5" data-testid="studio-pays-note">Tuition is handled by {offering.exclusive_studio_name || "the hosting studio"} — paid directly to Holly. There's nothing to pay here.</p>

                  <div className="rounded-xl bg-aviana-plum/5 border border-aviana-plum/25 px-4 py-3 mb-6" data-testid="modules-included-note">
                    <p className="text-[11px] tracking-[0.18em] uppercase text-aviana-plum">Included with registration</p>
                    <p className="text-aviana-inkSoft text-[13px] leading-[1.6] mt-1">
                      5 months of access to the <strong>Aerial Yoga Teacher Training Modules</strong> online course (exam, research paper, practice teaching log, class audit) — granted automatically when you complete the form. The 5-month window begins on the training's first day. Extensions are available if you need more time.
                    </p>
                  </div>

                  <button
                    onClick={() => goReserve()}
                    disabled={loading || offering.spots_left === 0}
                    className="btn-primary w-full"
                    data-testid="book-now-btn"
                  >
                    {loading
                      ? "Processing…"
                      : offering.spots_left === 0
                      ? "Waitlist"
                      : "Begin my registration →"}
                  </button>
                </>
              ) : (
                <>
                  <p className="overline mb-3">Investment</p>
                  {offering.deposit_only ? (
                    <div className="mb-4" data-testid="deposit-only-header">
                      <div className="flex items-baseline gap-3 mb-1">
                        <span className="font-display text-5xl text-aviana-ink">${offering.deposit_usd}</span>
                        <span className="text-aviana-muted text-sm">deposit</span>
                      </div>
                      <p className="text-[13px] text-aviana-muted italic">Full tuition confirmed once final group size is set.</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-3 mb-2 flex-wrap" data-testid="tuition-block">
                        <span className="font-display text-5xl text-aviana-ink">${offering.price_usd.toLocaleString()}</span>
                        <span className="text-aviana-muted text-sm">USD</span>
                        {onSale && (
                          <span className="text-aviana-muted text-[13px] line-through decoration-aviana-plum/40" data-testid="tuition-was">
                            was ${offering.price_regular_usd.toLocaleString()}
                          </span>
                        )}
                      </div>
                      {onSale && (
                        <div className="mt-1 mb-3" data-testid="sale-badge">
                          <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-aviana-goldDeep bg-aviana-gold/12 border border-aviana-gold/40 rounded-full px-3 py-1.5">
                            <Sparkles size={12} /> {saleLabel}
                          </p>
                          <p className="text-[12px] text-aviana-inkSoft mt-2 leading-[1.55]">
                            Tuition goes to <strong className="text-aviana-ink">${offering.price_regular_usd.toLocaleString()}</strong> on {saleEndDisplay}. Lock in today's rate to save <strong className="text-aviana-plum">${(offering.price_regular_usd - offering.price_usd).toLocaleString()}</strong>.
                          </p>
                          <SaleCountdown target={saleEnd} />
                        </div>
                      )}
                    </>
                  )}
                  {offering.deposit_usd && (
                    <p className="text-sm text-aviana-muted mb-6">
                      {offering.deposit_only
                        ? `Reserve your spot with a $${offering.deposit_usd} non-refundable deposit — remaining tuition ${balanceDueLine}`
                        : `Reserve your spot with a $${offering.deposit_usd} deposit — ${balanceDueLine}`}
                    </p>
                  )}
                  {offering.special_tuition_usd && (
                    <p className="mb-4 text-[12px] text-aviana-muted italic" data-testid="special-tuition-note">
                      Special tuition discount applies {offering.special_tuition_label || "for hosting-studio staff"}.
                    </p>
                  )}

                  {offering.deposit_usd && offering.deposit_only ? (
                    <div className="mt-2 mb-6" data-testid="deposit-only-note">
                      <div className="p-4 rounded-2xl border border-aviana-ink bg-aviana-surface text-center">
                        <div className="font-display text-xl text-aviana-ink">${offering.deposit_usd}</div>
                        <div className="text-xs text-aviana-muted mt-0.5">Non-refundable deposit</div>
                      </div>
                    </div>
                  ) : offering.deposit_usd ? (
                    <div className="mt-2 mb-6 grid grid-cols-2 gap-2" data-testid="payment-options">
                      <button
                        onClick={() => setPaymentType("deposit")}
                        className={`p-4 rounded-2xl border text-sm transition-all ${paymentType === "deposit" ? "border-aviana-ink bg-aviana-surface text-aviana-ink" : "border-aviana-border text-aviana-muted hover:border-aviana-plum"}`}
                        data-testid="pay-deposit-btn"
                      >
                        <div className="font-display text-xl">${offering.deposit_usd}</div>
                        <div className="text-xs">Deposit today</div>
                      </button>
                      <button
                        onClick={() => setPaymentType("full")}
                        className={`p-4 rounded-2xl border text-sm transition-all ${paymentType === "full" ? "border-aviana-ink bg-aviana-surface text-aviana-ink" : "border-aviana-border text-aviana-muted hover:border-aviana-plum"}`}
                        data-testid="pay-full-btn"
                      >
                        <div className="font-display text-xl">${offering.price_usd.toLocaleString()}</div>
                        <div className="text-xs">Pay in full</div>
                      </button>
                    </div>
                  ) : null}

                  <button
                    onClick={() => goReserve()}
                    disabled={loading || offering.spots_left === 0}
                    className="btn-primary w-full"
                    data-testid="book-now-btn"
                  >
                    {loading
                      ? "Processing…"
                      : offering.spots_left === 0
                      ? "Waitlist"
                      : isTraining
                      ? "Start your registration →"
                      : isRetreat
                      ? "Start your registration →"
                      : `Reserve · $${amountNow.toLocaleString()}`}
                  </button>
                </>
              )}

              <p className="text-[11px] text-aviana-muted text-center mt-4 leading-relaxed">
                {offering.studio_pays_tuition
                  ? "Your spot is held the moment you submit."
                  : (isTraining || isRetreat)
                  ? "We'll take you through a short intake form, then offer card (Stripe), Venmo, or Zelle. Full cancellation policy shown at the start of the form."
                  : "Secure Stripe checkout."}
              </p>

              {lowSeats && (
                <p className="text-center text-aviana-plum text-sm mt-4" data-testid="low-seats-warn">Only {seatsLeft} {seatsLeft === 1 ? "spot" : "spots"} remaining</p>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}


// Compact countdown showing days · hours · minutes until the sale expires.
// Ticks every 60s (no need for a per-second update — nothing here reads at
// that granularity and it keeps re-renders cheap even on the marketing page).
function SaleCountdown({ target }) {
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    if (!target) return undefined;
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return null;
  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return null;
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  const hours = Math.floor((diffMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const minutes = Math.floor((diffMs % (60 * 60 * 1000)) / (60 * 1000));
  const cell = (n, label) => (
    <div className="flex flex-col items-center min-w-[52px] rounded-lg bg-aviana-ink text-aviana-cream px-2 py-1.5">
      <span className="font-display text-xl leading-none tabular-nums">{String(n).padStart(2, "0")}</span>
      <span className="text-[9px] tracking-[0.2em] uppercase text-aviana-cream/70 mt-1">{label}</span>
    </div>
  );
  return (
    <div className="mt-3 flex items-center gap-2" data-testid="sale-countdown">
      {cell(days, "days")}
      {cell(hours, "hrs")}
      {cell(minutes, "min")}
    </div>
  );
}


// Share row at the bottom of every offering — same UX as the Journal
// article footer. Facebook / X / LinkedIn / Email / Copy link, all one tap.
function OfferingShareRow({ title, slug }) {
  const [copied, setCopied] = React.useState(false);
  const url = typeof window !== "undefined"
    ? `${window.location.origin}/offering/${slug}`
    : `https://avianayoga.com/offering/${slug}`;
  const shareText = `${title} — with Aviana Yoga`;
  const links = [
    { key: "x", label: "Share on X",
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`,
      icon: (<svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>) },
    { key: "facebook", label: "Share on Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, icon: <Facebook size={15} /> },
    { key: "linkedin", label: "Share on LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, icon: <Linkedin size={15} /> },
    { key: "email", label: "Share via email",
      href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`I thought you'd love this from Aviana:\n\n${url}`)}`, icon: <MailIcon size={15} /> },
  ];
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Could not copy link — try again"); }
  };
  return (
    <div className="mt-16 pt-8 border-t border-aviana-border" data-testid="offering-share-row">
      <p className="overline-plum mb-4">Share this offering</p>
      <div className="flex flex-wrap items-center gap-3">
        {links.map((l) => (
          <a key={l.key} href={l.href} target={l.key === "email" ? undefined : "_blank"} rel="noopener noreferrer" aria-label={l.label} title={l.label}
             className="w-11 h-11 rounded-full border border-aviana-border text-aviana-inkSoft hover:text-aviana-plum hover:border-aviana-plum bg-aviana-cream/60 hover:bg-aviana-cream flex items-center justify-center transition-all"
             data-testid={`offering-share-${l.key}`}>
            {l.icon}
          </a>
        ))}
        <button onClick={copy} aria-label={copied ? "Link copied" : "Copy link"} title={copied ? "Copied" : "Copy link"}
                className={`w-11 h-11 rounded-full border flex items-center justify-center transition-all ${copied ? "border-aviana-plum bg-aviana-plum/10 text-aviana-plum" : "border-aviana-border text-aviana-inkSoft hover:text-aviana-plum hover:border-aviana-plum bg-aviana-cream/60 hover:bg-aviana-cream"}`}
                data-testid="offering-share-copy">
          {copied ? <Check size={15} /> : <Link2 size={15} />}
        </button>
      </div>
    </div>
  );
}
